// src/hooks/useSSE.ts
import { useEffect, useRef, useState } from 'react'
import api from '@/api/url'
import { useAuthStore } from '@/store/authStore'
import { LOCAL_STORAGE_KEYS, safeLocalStorage } from '@/constants/storageKeys'

type MessageData = any

interface UseSSEOptions {
  maxRetries?: number
  heartbeatInterval?: number // ms
  autoConnect?: boolean // 자동 연결 여부
}

export const useSSE = (options: UseSSEOptions = {}) => {
  const {
    maxRetries = 5,
    heartbeatInterval = 30_000, // 30초
    autoConnect = true // 기본값은 자동 연결
  } = options

  const [messages, setMessages] = useState<MessageData[]>([])
  const [isConnected, setIsConnected] = useState(false)

  const abortControllerRef = useRef<AbortController | null>(null)
  const retryCountRef = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastEventIdRef = useRef<string | null>(safeLocalStorage.get(LOCAL_STORAGE_KEYS.LAST_EVENT_ID))

  /** SSE 연결 */
  const connectSSE = async () => {
    try {
      const token = useAuthStore.getState().getAccessToken()
      if (!token) throw new Error('No access token found')

      const url = api.userSSE.GET_USER_SSE_URL
      const finalUrl = lastEventIdRef.current
        ? `${url}${url.includes('?') ? '&' : '?'}lastEventId=${lastEventIdRef.current}`
        : url

      abortControllerRef.current = new AbortController()

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        signal: abortControllerRef.current.signal
      })

      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`)
      }

      console.info('[SSE] Connected via fetch')
      setIsConnected(true)
      retryCountRef.current = 0
      resetHeartbeat()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const parts = buffer.split('\n\n')
        buffer = parts.pop() || ''

        for (const part of parts) {
          handleEvent(part)
          resetHeartbeat()
        }
      }
    } catch (error: any) {
      // AbortError는 정상적인 연결 종료이므로 무시
      if (error?.name === 'AbortError') {
        console.info('[SSE] Connection closed')
        cleanupConnection()
        return
      }

      console.error('[SSE] Error:', error)
      cleanupConnection()

      if (retryCountRef.current < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30_000)
        console.warn(`[SSE] Reconnecting in ${delay / 1000}s...`)
        reconnectTimeoutRef.current = setTimeout(() => {
          retryCountRef.current++
          connectSSE()
        }, delay)
      } else {
        console.error('[SSE] Max retries reached. Stopping reconnect.')
      }
    }
  }

  /** 이벤트 파서 */
  const handleEvent = (raw: string) => {
    const lines = raw.split('\n')
    let eventId: string | null = null
    let eventType: string | null = null
    let eventData = ''

    for (const line of lines) {
      if (line.startsWith('id:')) {
        eventId = line.slice(3).trim()
      } else if (line.startsWith('event:')) {
        eventType = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        eventData += line.slice(5).trim()
      } else if (line.startsWith(':')) {
        // : ping 형식 처리
        const content = line.slice(1).trim()
        if (content === 'ping') {
          console.debug('[SSE] Heartbeat')
          return
        }
      }
    }

    // connected 이벤트 처리
    if (eventType === 'connected') {
      console.info('[SSE] Connected event received:', eventData)
      return
    }

    // risk 이벤트 처리
    if (eventType === 'risk' && eventData) {
      try {
        const data = JSON.parse(eventData)
        setMessages(prev => [...prev, { type: 'risk', ...data }])
      } catch {
        console.warn('[SSE] Failed to parse risk event:', eventData)
      }
    } else if (eventType === 'mission' && eventData) {
      try {
        const data = JSON.parse(eventData)
        setMessages(prev => [...prev, { type: 'mission', data: eventData }])
        console.log(data)
      } catch {
        console.warn('[SSE] Failed to parse risk event:', eventData)
      }
    } else if (eventData && eventData !== 'ping') {
      try {
        const data = JSON.parse(eventData)
        setMessages(prev => [...prev, data])
      } catch {
        console.warn('[SSE] Non-JSON message:', eventData)
      }
    }

    if (eventId) {
      lastEventIdRef.current = eventId
      safeLocalStorage.set(LOCAL_STORAGE_KEYS.LAST_EVENT_ID, eventId)
    }
  }

  /** 하트비트 체크 */
  const resetHeartbeat = () => {
    if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current)
    heartbeatTimeoutRef.current = setTimeout(() => {
      console.warn('[SSE] Heartbeat timeout, reconnecting...')
      cleanupConnection()
      connectSSE()
    }, heartbeatInterval)
  }

  /** 기존 연결 정리 */
  const cleanupConnection = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current)
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
    setIsConnected(false)
  }

  /** 수동 연결 시작 */
  const connect = () => {
    if (!isConnected && !abortControllerRef.current) {
      connectSSE()
    }
  }

  /** 수동 연결 종료 */
  const disconnect = () => {
    retryCountRef.current = maxRetries // 재연결 방지
    cleanupConnection()
  }

  useEffect(() => {
    if (autoConnect) {
      connectSSE()
    }
    return () => cleanupConnection()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { messages, isConnected, connect, disconnect }
}
